import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StaffNavBar from "../components/StaffNavBar";
import { AdminPageBody, AdminPanel, AdminLoadingState } from "../components/AdminPageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faNewspaper,
  faClipboardCheck,
  faArrowTrendUp,
  faCheckCircle,
  faKey,
  faReceipt,
  faEnvelope,
  faTriangleExclamation,
  faTruckFast,
  faServer,
  faBoxesStacked,
  faBell,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import { useStaffDashboard } from "../store/staff";

const StaffDashboard = () => {
  const navigate = useNavigate();
  const {
    staffDashboard,
    staffDashboardLoading,
    staffDashboardError,
    fetchStaffDashboard,
  } = useStaffDashboard();

  // GET /api/staff/dashboard
  useEffect(() => {
    // examType is omitted when unset: the backend validates it as a strict enum
    // (WAEC|NECO|NABTEB) and an empty value would be rejected.
    fetchStaffDashboard({
      recentPurchasesLimit: 10,
      recentFailedEmailsLimit: 10,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the documented operational payload defensively so an unexpected key
  // never breaks the page.
  const d = staffDashboard || {};
  const inventory = d.inventory || {};
  const purchases = d.purchases || {};
  const fulfillment = d.fulfillment || {};
  const emails = d.emails || {};
  const providers = d.providers || {};
  const smtp = providers.smtp || {};
  const pendingContent = d.pendingContent || {};
  const draftContent = d.draftContent || {};
  const alerts = Array.isArray(d.alerts) ? d.alerts : [];
  const recent = d.recent || {};
  const recentPurchases = Array.isArray(recent.purchases) ? recent.purchases : [];
  const recentFulfillment = Array.isArray(recent.fulfillmentEvents)
    ? recent.fulfillmentEvents
    : [];
  const recentFailedEmails = Array.isArray(recent.failedEmails) ? recent.failedEmails : [];
  const unreadNotifications = d.unreadNotifications;

  const num = (v) =>
    v === undefined || v === null || v === "" || Number.isNaN(Number(v))
      ? "—"
      : Number(v).toLocaleString();

  const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

  const accents = {
    blue: {
      icon: "bg-blue-50 text-blue-600 ring-blue-100",
      bar: "bg-gradient-to-r from-blue-500 to-cyan-400",
      glow: "from-blue-500/15 to-cyan-400/5",
      text: "text-blue-600",
    },
    green: {
      icon: "bg-emerald-50 text-emerald-600 ring-emerald-100",
      bar: "bg-gradient-to-r from-emerald-500 to-teal-400",
      glow: "from-emerald-500/15 to-teal-400/5",
      text: "text-emerald-600",
    },
    orange: {
      icon: "bg-amber-50 text-amber-600 ring-amber-100",
      bar: "bg-gradient-to-r from-amber-500 to-orange-400",
      glow: "from-amber-500/15 to-orange-400/5",
      text: "text-amber-600",
    },
    red: {
      icon: "bg-rose-50 text-rose-600 ring-rose-100",
      bar: "bg-gradient-to-r from-rose-500 to-red-400",
      glow: "from-rose-500/15 to-red-400/5",
      text: "text-rose-600",
    },
  };
  const accent = (color) => accents[color] || accents.blue;
  const pct = (part, total) => {
    const n = Number(part) || 0;
    const d = Number(total) || 0;
    return d > 0 ? Math.min(100, Math.round((n / d) * 100)) : 0;
  };
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  const paymentTone = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "COMPLETED":
        return "bg-emerald-50 text-emerald-700 ring-emerald-100";
      case "PENDING":
        return "bg-amber-50 text-amber-700 ring-amber-100";
      case "FAILED":
        return "bg-rose-50 text-rose-700 ring-rose-100";
      case "ABANDONED":
        return "bg-slate-100 text-slate-600 ring-slate-200";
      default:
        return "bg-slate-100 text-slate-600 ring-slate-200";
    }
  };

  const fulfillmentTone = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "FULFILLED":
        return "bg-emerald-50 text-emerald-700 ring-emerald-100";
      case "PENDING_FULFILLMENT":
        return "bg-amber-50 text-amber-700 ring-amber-100";
      case "INVENTORY_SHORTAGE":
        return "bg-rose-50 text-rose-700 ring-rose-100";
      default:
        return "bg-slate-100 text-slate-600 ring-slate-200";
    }
  };

  const severityClasses = {
    critical: "border-red-200 bg-red-50 text-red-700",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    info: "border-blue-200 bg-blue-50 text-blue-700",
  };
  const severity = (key) => severityClasses[key] || severityClasses.info;

  // Headline metrics — Exam PIN inventory, purchases and email failures.
  const metrics = [
    {
      label: "Exam PINs Available",
      value: num(inventory.available),
      note: inventory.lowStock ? "Low stock" : null,
      caption: `${num(inventory.total)} total`,
      icon: faKey,
      color: "blue",
    },
    {
      label: "Exam PINs Allocated",
      value: num(inventory.allocated),
      icon: faCheckCircle,
      color: "green",
    },
    {
      label: "Total Purchases",
      value: num(purchases.total),
      icon: faReceipt,
      color: "orange",
    },
    {
      label: "Failed Emails",
      value: num(emails.failed),
      icon: faTriangleExclamation,
      color: "red",
    },
  ];

  // Secondary operational breakdown groups.
  const groups = [
    {
      title: "Purchases",
      icon: faReceipt,
      color: "blue",
      progress: pct(purchases.successful, purchases.total),
      progressLabel: "Successful payments",
      stats: [
        { label: "Total", value: purchases.total },
        { label: "Successful", value: purchases.successful },
        { label: "Pending", value: purchases.pending },
        { label: "Failed", value: purchases.failed },
        { label: "Today", value: purchases.today },
        { label: "Today successful", value: purchases.todaySuccessful },
      ],
    },
    {
      title: "Fulfillment",
      icon: faTruckFast,
      color: "green",
      progress: pct(
        fulfillment.fulfilled,
        (Number(fulfillment.fulfilled) || 0) +
          (Number(fulfillment.pendingFulfillment) || 0) +
          (Number(fulfillment.failedFulfillment) || 0)
      ),
      progressLabel: "Fulfilled",
      stats: [
        { label: "Fulfilled", value: fulfillment.fulfilled },
        { label: "Pending", value: fulfillment.pendingFulfillment },
        { label: "Failed", value: fulfillment.failedFulfillment },
        { label: "Inventory exhausted", value: fulfillment.inventoryExhausted },
      ],
    },
    {
      title: "Emails",
      icon: faEnvelope,
      color: "orange",
      progress: pct(emails.sent, emails.total),
      progressLabel: "Delivered",
      stats: [
        { label: "Total", value: emails.total },
        { label: "Sent", value: emails.sent },
        { label: "Pending", value: emails.pending },
        { label: "Retrying", value: emails.retrying },
        { label: "Failed", value: emails.failed },
      ],
    },
    {
      title: "Email Providers",
      icon: faServer,
      color: "blue",
      progress: pct(smtp.succeeded, smtp.attempts),
      progressLabel: "SMTP success",
      stats: [
        { label: "SMTP attempts", value: smtp.attempts },
        { label: "SMTP succeeded", value: smtp.succeeded },
        { label: "SMTP failed", value: smtp.failed },
        { label: "Fallback attempts", value: providers.fallbackAttempts },
      ],
    },
  ];

  const contentRows = [
    { label: "Courses", type: "courses", pending: pendingContent.courses, draft: draftContent.courses },
    { label: "Books", type: "books", pending: pendingContent.books, draft: draftContent.books },
    {
      label: "Journals",
      type: "journals",
      pending: pendingContent.journals,
      draft: draftContent.journals,
    },
    {
      label: "Learning Paths",
      type: "learning-paths",
      pending: pendingContent.learningPaths,
      draft: draftContent.learningPaths,
    },
  ];

  const pendingContentTotal = contentRows.reduce(
    (sum, row) => sum + (Number(row.pending) || 0),
    0
  );
  const draftContentTotal = contentRows.reduce(
    (sum, row) => sum + (Number(row.draft) || 0),
    0
  );

  const quickActions = [
    {
      title: "Exam PINs",
      description: "Manage PIN inventory and uploads",
      icon: faKey,
      color: "blue",
      path: "/staff/exam-pins",
      stat: `${num(inventory.available)} available · ${num(inventory.total)} total`,
    },
    {
      title: "Exam Purchases",
      description: "Track purchases and fulfillment",
      icon: faReceipt,
      color: "green",
      path: "/staff/exam-purchases",
      stat: `${num(purchases.total)} total · ${num(fulfillment.pendingFulfillment)} pending`,
    },
    {
      title: "Email Deliveries",
      description: "Retry failed PIN delivery emails",
      icon: faEnvelope,
      color: "orange",
      path: "/staff/emails",
      stat: `${num(emails.failed)} failed · ${num(emails.retrying)} retrying`,
    },
    {
      title: "Content Review",
      description: "Review pending submissions",
      icon: faClipboardCheck,
      color: "red",
      path: "/staff/content-review",
      stat: `${num(pendingContentTotal)} pending · ${num(draftContentTotal)} draft`,
    },
    {
      title: "News Management",
      description: "Create and manage blog articles",
      icon: faNewspaper,
      color: "blue",
      path: "/staff/news-management",
      stat: "Articles",
    },
  ];

  return (
    <div>
      <StaffNavBar
        title="Staff Dashboard"
        context="Live Exam PIN inventory, purchases and email delivery."
      />
      <AdminPageBody>
        {/* Hero */}
        <section className="relative overflow-hidden rounded bg-gradient-to-br from-[#0b1220] via-[#16213A] to-[#1e3a8a] p-6 text-white shadow-xl shadow-slate-900/10">
          <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-blue-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-400/10 blur-3xl" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">

                <span className="text-xs text-white/50">{todayLabel}</span>
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80">
                  <span className="font-semibold text-white">{num(inventory.available)}</span> PINs available
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80">
                  <span className="font-semibold text-white">{num(pendingContentTotal)}</span> awaiting review
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/80">
                  <span className="font-semibold text-white">{num(unreadNotifications)}</span> unread
                </span>
                {inventory.lowStock ? (
                  <span className="rounded-full border border-amber-300/30 bg-amber-400/15 px-3 py-1.5 text-xs font-semibold text-amber-200">
                    Low stock
                  </span>
                ) : null}
              </div>
            </div>

            <button
              className="inline-flex min-h-11 items-center justify-center gap-2 self-start rounded-xl border border-white/20 bg-white/10 
                p-2.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60 lg:self-auto"
              disabled={staffDashboardLoading}
              onClick={() =>
                fetchStaffDashboard({
                  recentPurchasesLimit: 10,
                  recentFailedEmailsLimit: 10,
                })
              }
            >
              <FontAwesomeIcon
                icon={faRotate}
                className={staffDashboardLoading ? "animate-spin" : ""}
              />
              {staffDashboardLoading ? "Refreshing…" : "Refresh data"}
            </button>
          </div>
        </section>

        {staffDashboardError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />
            <span>{staffDashboardError}</span>
          </div>
        )}

        {staffDashboardLoading && !staffDashboard ? (
          <AdminLoadingState />
        ) : (
          <>
            {/* Operational alerts */}
            {alerts.length > 0 && (
              <div className="grid gap-3">
                {alerts.map((a, i) => (
                  <div
                    key={a.key ?? i}
                    className={`flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-sm shadow-sm ${severity(
                      a.severity
                    )}`}
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/70">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                    </span>
                    <span>
                      {a.area && <span className="font-semibold">{a.area}: </span>}
                      {a.message || a.key}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Metric cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {metrics.map((m) => {
                const theme = accent(m.color);
                return (
                  <div
                    key={m.label}
                    className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    <div className={`absolute inset-x-0 top-0 h-1 ${theme.bar}`} />
                    <div
                      className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${theme.glow} blur-2xl`}
                    />
                    <div className="relative flex items-start justify-between">
                      <span
                        className={`flex h-11 w-11 items-center justify-center rounded-xl ring-1 ${theme.icon}`}
                      >
                        <FontAwesomeIcon icon={m.icon} className="text-lg" />
                      </span>
                      {m.note ? (
                        <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-rose-600 ring-1 ring-rose-100">
                          {m.note}
                        </span>
                      ) : null}
                    </div>
                    <div className="relative mt-4 text-3xl font-bold tracking-tight text-slate-900">
                      {m.value}
                    </div>
                    <div className="relative mt-1 text-sm font-medium text-slate-600">
                      {m.label}
                    </div>
                    <div className="relative mt-0.5 text-xs text-slate-400">
                      {m.caption || "\u00a0"}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Operational breakdown */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              {groups.map((g) => {
                const theme = accent(g.color);
                return (
                  <section
                    key={g.title}
                    className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${theme.icon}`}
                        >
                          <FontAwesomeIcon icon={g.icon} className="text-xs" />
                        </span>
                        <h3 className="m-0 truncate text-xs font-semibold text-slate-900">
                          {g.title}
                        </h3>
                      </div>
                      <span className={`shrink-0 text-[11px] font-bold ${theme.text}`}>
                        {g.progress}%
                      </span>
                    </div>

                    <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${theme.bar} transition-all duration-500`}
                        style={{ width: `${g.progress}%` }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] text-slate-400">{g.progressLabel}</p>

                    <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-2.5">
                      {g.stats.map((s) => (
                        <div key={s.label} className="min-w-0">
                          <dd className="m-0 text-sm font-bold leading-tight tracking-tight text-slate-900">
                            {num(s.value)}
                          </dd>
                          <dt className="mt-0.5 truncate text-[10px] leading-tight text-slate-500">
                            {s.label}
                          </dt>
                        </div>
                      ))}
                    </dl>
                  </section>
                );
              })}
            </div>

            {/* Content queue + notifications */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm lg:col-span-2">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
                      <FontAwesomeIcon icon={faBoxesStacked} />
                    </span>
                    <div>
                      <h3 className="m-0 text-sm font-semibold text-slate-900">Content Queue</h3>
                      <p className="m-0 text-xs text-slate-400">
                        {num(pendingContentTotal)} pending · {num(draftContentTotal)} drafts
                      </p>
                    </div>
                  </div>
                  <button
                    className="text-xs font-semibold text-[#004ac6] hover:underline"
                    onClick={() => navigate("/staff/content-review")}
                  >
                    Open review queue
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {contentRows.map((r) => (
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => navigate(`/staff/content-review?type=${r.type}`)}
                      className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-slate-50"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                        <FontAwesomeIcon icon={faClipboardCheck} className="text-xs" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                        {r.label}
                      </span>
                      <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-100">
                        {num(r.pending)} pending
                      </span>
                      <span className="hidden rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 sm:inline">
                        {num(r.draft)} drafts
                      </span>
                      <FontAwesomeIcon
                        icon={faArrowTrendUp}
                        className="text-xs text-slate-300"
                      />
                    </button>
                  ))}
                </div>
              </section>

              <section className="relative flex flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-[#16213A] to-[#1e3a8a] p-5 text-white shadow-sm">
                <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-blue-400/20 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-blue-100 ring-1 ring-white/15">
                      <FontAwesomeIcon icon={faBell} />
                    </span>
                    <span className="text-sm font-semibold text-white/90">
                      Unread notifications
                    </span>
                  </div>
                  <div className="mt-5 text-4xl font-bold tracking-tight">
                    {num(unreadNotifications)}
                  </div>
                  <p className="mt-1 text-xs text-white/55">
                    Mentions, assignments and system updates waiting for you.
                  </p>
                </div>
                <button
                  className="relative mt-6 inline-flex min-h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                  onClick={() => navigate("/staff/notifications")}
                >
                  View notifications
                </button>
              </section>
            </div>

            {/* Quick Actions */}
            <div>
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <h2 className="m-0 text-lg font-semibold text-slate-900">Quick Actions</h2>
                  <p className="m-0 mt-1 text-sm text-slate-500">
                    Jump straight into the work that needs you.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {quickActions.map((action) => {
                  const theme = accent(action.color);
                  return (
                    <button
                      key={action.title}
                      type="button"
                      onClick={() => navigate(action.path)}
                      className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg"
                    >
                      <div
                        className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${theme.glow} blur-2xl`}
                      />
                      <div className="relative flex items-start justify-between">
                        <span
                          className={`flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${theme.icon}`}
                        >
                          <FontAwesomeIcon icon={action.icon} className="text-xl" />
                        </span>
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition group-hover:bg-[#16213A] group-hover:text-white">
                          <FontAwesomeIcon icon={faArrowTrendUp} className="text-xs" />
                        </span>
                      </div>
                      <h3 className="relative mt-4 text-base font-semibold text-slate-900">
                        {action.title}
                      </h3>
                      <p className="relative mt-1 min-h-[2.5rem] text-sm text-slate-500">
                        {action.description}
                      </p>
                      <div className={`relative mt-3 text-xs font-semibold ${theme.text}`}>
                        {action.stat}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recent activity from /api/staff/dashboard */}
            <div className="space-y-6">
              {/* Recent purchases — full-width rows */}
              <AdminPanel className="overflow-hidden rounded-2xl border-slate-200/80">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                      <FontAwesomeIcon icon={faReceipt} />
                    </span>
                    <div>
                      <h3 className="m-0 text-sm font-semibold text-slate-900">
                        Recent Purchases
                      </h3>
                      <p className="m-0 text-xs text-slate-400">
                        {recentPurchases.length} latest live order
                        {recentPurchases.length === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/staff/exam-purchases")}
                    className="text-xs font-semibold text-[#004ac6] hover:underline"
                  >
                    View all purchases
                  </button>
                </div>

                {recentPurchases.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <p className="m-0 text-sm font-medium text-slate-600">No recent purchases</p>
                    <p className="m-0 mt-1 text-xs text-slate-400">
                      New Exam PIN orders will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left text-sm">
                      <thead className="bg-slate-50/80 text-[11px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-5 py-2.5 font-semibold">Order</th>
                          <th className="px-4 py-2.5 font-semibold">Date</th>
                          <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                          <th className="px-4 py-2.5 font-semibold">Payment</th>
                          <th className="px-5 py-2.5 font-semibold">Fulfillment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {recentPurchases.slice(0, 10).map((p, i) => (
                          <tr key={p.id ?? i} className="transition hover:bg-slate-50/70">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                  <FontAwesomeIcon icon={faReceipt} className="text-xs" />
                                </span>
                                <div className="min-w-0">
                                  <p className="m-0 truncate font-semibold text-slate-900">
                                    {p.reference || p.id || "—"}
                                  </p>
                                  <p className="m-0 truncate text-xs text-slate-500">
                                    {p.customerName || "Unknown customer"}
                                    {p.customerEmail ? ` · ${p.customerEmail}` : ""}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                              {fmtDate(p.createdAt)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-800">
                              {p.amount != null ? `₦${Number(p.amount).toLocaleString()}` : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ${paymentTone(
                                  p.paymentStatus
                                )}`}
                              >
                                {p.paymentStatus || "UNKNOWN"}
                              </span>
                            </td>
                            <td className="px-5 py-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ${fulfillmentTone(
                                  p.fulfillmentStatus
                                )}`}
                              >
                                {p.fulfillmentStatus || "UNKNOWN"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </AdminPanel>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Failed emails */}
                <AdminPanel className="overflow-hidden rounded-2xl border-slate-200/80">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                        <FontAwesomeIcon icon={faEnvelope} />
                      </span>
                      <div>
                        <h3 className="m-0 text-sm font-semibold text-slate-900">
                          Recent Failed Emails
                        </h3>
                        <p className="m-0 text-xs text-slate-400">
                          {recentFailedEmails.length} needing a retry
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/staff/emails")}
                      className="text-xs font-semibold text-[#004ac6] hover:underline"
                    >
                      View all
                    </button>
                  </div>

                  {recentFailedEmails.length === 0 ? (
                    <div className="px-5 py-10 text-center">
                      <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </span>
                      <p className="m-0 mt-3 text-sm font-medium text-slate-600">
                        No failed emails
                      </p>
                      <p className="m-0 mt-1 text-xs text-slate-400">
                        Every PIN delivery email is healthy.
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {recentFailedEmails.slice(0, 10).map((e, i) => (
                        <li key={e.id ?? i} className="px-5 py-3">
                          <div className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                              <FontAwesomeIcon icon={faTriangleExclamation} className="text-xs" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="m-0 truncate text-sm font-semibold text-slate-900">
                                  {e.recipient || "—"}
                                </p>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                  {e.provider || "—"}
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  {e.attempts != null ? `${e.attempts} attempts` : ""}
                                </span>
                              </div>
                              <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                                {e.reference || "No order reference"}
                              </p>
                              <p
                                className="m-0 mt-1 line-clamp-2 text-xs text-rose-600"
                                title={e.lastError || ""}
                              >
                                {e.lastError || "No error message recorded."}
                              </p>
                            </div>
                            <span className="shrink-0 text-[11px] text-slate-400">
                              {fmtDate(e.failedAt)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </AdminPanel>

                {/* Fulfillment events */}
                <AdminPanel className="overflow-hidden rounded-2xl border-slate-200/80">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                        <FontAwesomeIcon icon={faTruckFast} />
                      </span>
                      <div>
                        <h3 className="m-0 text-sm font-semibold text-slate-900">
                          Fulfillment Events
                        </h3>
                        <p className="m-0 text-xs text-slate-400">
                          {recentFulfillment.length} latest PIN activities
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/staff/exam-purchases")}
                      className="text-xs font-semibold text-[#004ac6] hover:underline"
                    >
                      View all
                    </button>
                  </div>

                  {recentFulfillment.length === 0 ? (
                    <div className="px-5 py-10 text-center">
                      <p className="m-0 text-sm font-medium text-slate-600">
                        No recent fulfillment activity
                      </p>
                      <p className="m-0 mt-1 text-xs text-slate-400">
                        PIN allocations and retries will appear here.
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {recentFulfillment.slice(0, 10).map((ev, i) => (
                        <li key={ev.id ?? i} className="px-5 py-3">
                          <div className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                              <FontAwesomeIcon icon={faTruckFast} className="text-xs" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="m-0 truncate text-sm font-semibold text-slate-900">
                                  {ev.action || "—"}
                                </p>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                  {ev.actorType || "SYSTEM"}
                                </span>
                              </div>
                              <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                                {ev.actorId || "No actor id"}
                              </p>
                              <p
                                className="m-0 mt-1 truncate text-xs text-slate-400"
                                title={ev.metadata ? JSON.stringify(ev.metadata) : ""}
                              >
                                {ev.metadata ? JSON.stringify(ev.metadata) : "No additional details"}
                              </p>
                            </div>
                            <span className="shrink-0 text-[11px] text-slate-400">
                              {fmtDate(ev.createdAt)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </AdminPanel>
              </div>
            </div>

          </>
        )}
      </AdminPageBody>
    </div>
  );
};

export default StaffDashboard;
